import { useEffect } from "react";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { usePlanStatus } from "@/react/hooks/usePlanStatus";
import { CheckIfPlanStatusSyncedWorkflowsApp } from "@circle-react-shared/UpgradePlan/CheckIfPlanStatusSyncedWorkflowsApp";
import { UPGRADE_PATHS } from "@circle-react-shared/UpgradePlan/constants";
import { BlockPlanTrialDowngrade } from "./BlockPlanTrialDowngrade";
import { CanDowngrade } from "./CanDowngrade";
import { CancelDowngradeScheduleWrapper } from "./CancelDowngradeScheduleWrapper";
import { ContactSupport } from "./ContactSupport";
import { EmailHubSubscriptionCheck } from "./EmailHubSubscriptionCheck";
import { Loader } from "./Loader";
import { PlanDetails } from "./PlanDetails";
import { ScheduledDowngradeConfirmationModal } from "./PlanDetails/ScheduledDowngradeConfirmationModal";
import { PlanDowngradeScheduleSuccess } from "./PlanDowngradeScheduleSuccess";
import { PlanUpdateSuccess } from "./PlanUpdateSuccess";
import { Proration } from "./Proration";
import { Renewal } from "./Renewal";
import { useUpgradePlanContext } from "./UpgradePlanContext";
import { useBlockPlanTrialDowngrade } from "./hooks/useBlockPlanTrialDowngrade";
import { useCancelDowngradeScheduledHandler } from "./hooks/useCancelDowngradeScheduledHandler";
import { useUpgradePlanForm } from "./hooks/useUpgradePlanForm";
import { isDowngrade } from "./planActionHelpers";
import type { CancelScheduledDowngradeModalSource, UpgradePlan } from "./types";
import { CANCEL_SCHEDULED_DOWNGRADE_SOURCES } from "./types";
import { useUpgradeFlowAnalytics } from "./useUpgradeFlowAnalytics";

const i18nRoot = "pricing_plans.upgrade_modal";

export interface UpgradeFlowProps {
  handleOnSuccess: () => void;
  modal: any;
  subheading?: string;
  successButtonLabel?: string;
}

export const UpgradeFlow = ({
  handleOnSuccess,
  modal,
  subheading,
  successButtonLabel,
}: UpgradeFlowProps) => {
  const {
    plan,
    usedForWorkflows: isUsedForWorkflows,
    showPlanDetails: shouldShowPlanDetails,
    source,
    canCheckoutInstantly,
    isEmailHubOnlyUpgrade,
    shouldSkipProrationStep,
    isRenewal,
  } = useUpgradePlanContext();

  const { shouldShowProration } = usePlanStatus();

  const shouldShowProrationDetails = Boolean(
    shouldShowProration(plan) && !shouldSkipProrationStep,
  );

  const { shouldBlockPlanTrialDowngrade } = useBlockPlanTrialDowngrade();

  const { isScheduledDowngradeActive } = useCancelDowngradeScheduledHandler({
    source: (source ||
      CANCEL_SCHEDULED_DOWNGRADE_SOURCES.PLAN_DETAILS) as CancelScheduledDowngradeModalSource,
  });

  const {
    onSubmitHandler,
    emailHubOnlySubmitHandler,
    changePlan: fullChangePlan,
    loadingMessage,
  } = useUpgradePlanForm({
    usedForWorkflows: isUsedForWorkflows || false,
    showProration: shouldShowProrationDetails,
    source: source || "",
  });

  const handleChangePlan = (plan: UpgradePlan) => {
    fullChangePlan(plan, null);
  };

  useEffect(() => {
    if (canCheckoutInstantly) {
      onSubmitHandler();
    }
  }, [canCheckoutInstantly]);

  const location = useLocation();
  const { trackUpgradeFlowPathChange } = useUpgradeFlowAnalytics();

  const onSubmitHandlerPlanDetails = () => {
    if (isEmailHubOnlyUpgrade) {
      return emailHubOnlySubmitHandler();
    }
    return onSubmitHandler();
  };

  useEffect(() => {
    trackUpgradeFlowPathChange(location.pathname);
  }, [location, trackUpgradeFlowPathChange]);

  const initPath = () => {
    switch (true) {
      case isRenewal:
        return UPGRADE_PATHS.RENEWAL;
      case isScheduledDowngradeActive:
        return UPGRADE_PATHS.CANCEL_SCHEDULED_DOWNGRADE_MODAL;
      case shouldBlockPlanTrialDowngrade:
        return UPGRADE_PATHS.BLOCK_PLAN_TRIAL_DOWNGRADE;
      case shouldShowPlanDetails:
        return UPGRADE_PATHS.PLAN_DETAILS;
      case shouldShowProrationDetails:
        return UPGRADE_PATHS.PRORATION;
      default:
        return UPGRADE_PATHS.LOADING;
    }
  };

  return (
    <Switch>
      <Route exact path={UPGRADE_PATHS.CANCEL_SCHEDULED_DOWNGRADE_MODAL}>
        <CancelDowngradeScheduleWrapper
          source={
            (source ||
              CANCEL_SCHEDULED_DOWNGRADE_SOURCES.PLAN_DETAILS) as CancelScheduledDowngradeModalSource
          }
          hideModal={modal.hide}
        />
      </Route>
      <Route exact path={UPGRADE_PATHS.BLOCK_PLAN_TRIAL_DOWNGRADE}>
        <BlockPlanTrialDowngrade />
      </Route>
      <Route exact path={UPGRADE_PATHS.PLAN_DETAILS}>
        <PlanDetails
          subheading={subheading}
          hideModal={modal.hide}
          onSubmitHandler={onSubmitHandlerPlanDetails}
        />
      </Route>
      <Route exact path={UPGRADE_PATHS.SCHEDULED_DOWNGRADE_CONFIRMATION}>
        <ScheduledDowngradeConfirmationModal
          hideModal={modal.hide}
          onSubmitHandler={onSubmitHandler}
          plan={plan}
        />
      </Route>
      <Route path={UPGRADE_PATHS.EMAIL_HUB_SUBSCRIPTION_CHECK}>
        <EmailHubSubscriptionCheck />
      </Route>
      <Route path={UPGRADE_PATHS.PRORATION}>
        <Proration
          hideModal={modal.hide}
          onSubmitHandler={() => onSubmitHandler()}
        />
      </Route>
      <Route path={UPGRADE_PATHS.CAN_DOWNGRADE}>
        <CanDowngrade
          plan={plan}
          changePlan={handleChangePlan}
          hideModal={modal.hide}
        />
      </Route>
      <Route exact path={UPGRADE_PATHS.LOADING}>
        <Loader
          title={loadingMessage.title}
          subtitle={loadingMessage.subtitle}
        />
      </Route>
      <Route exact path={UPGRADE_PATHS.WORKFLOW_APP_SYNCING}>
        <CheckIfPlanStatusSyncedWorkflowsApp planTier={plan?.tier} />
      </Route>
      <Route exact path={UPGRADE_PATHS.ERROR}>
        <ContactSupport title={t([i18nRoot, "errors.general_error.title"])} />
      </Route>
      <Route exact path={UPGRADE_PATHS.UPGRADE_SUCCESSFUL}>
        <PlanUpdateSuccess
          buttonLabel={successButtonLabel}
          onSuccess={handleOnSuccess}
        />
      </Route>
      <Route exact path={UPGRADE_PATHS.SCHEDULED_DOWNGRADE_SUCCESS}>
        <PlanDowngradeScheduleSuccess
          buttonLabel={successButtonLabel}
          onSuccess={handleOnSuccess}
          isPlanActionDowngrade={isDowngrade(plan?.plan_action)}
        />
      </Route>
      <Route exact path={UPGRADE_PATHS.RENEWAL}>
        <Renewal source={source} />
      </Route>
      <Redirect to={initPath()} />
    </Switch>
  );
};
